/**
 * <treat-shower> drops a short shower of tiny treats (hearts, sparkles, sprinkles, mini cupcakes)
 * over the Sweet Rewards window and the chat window each time someone taps My Rewards or the chat
 * icon. Both windows belong to apps, so the shower is a page layer laid exactly over the open window.
 * It starts from the tap itself (with an open-state check as a backup) and retries until the
 * window has finished opening. Purely decorative; skipped for visitors who prefer reduced motion.
 */
const HEART = '<svg viewBox="0 0 48 48"><path d="M24 42S6 31 6 18a9 9 0 0 1 18-3 9 9 0 0 1 18 3c0 13-18 24-18 24z" fill="COLOR"/></svg>';
const STAR = '<svg viewBox="0 0 24 24"><path d="M12 0C13 8 16 11 24 12C16 13 13 16 12 24C11 16 8 13 0 12C8 11 11 8 12 0Z" fill="COLOR"/></svg>';
const SPRINKLE = '<svg viewBox="0 0 24 24"><rect x="2" y="9" width="20" height="6" rx="3" fill="COLOR"/></svg>';
const CUPCAKE = '<svg viewBox="0 0 48 48"><path d="M11 26h26l-4 17a3 3 0 0 1-3 2H18a3 3 0 0 1-3-2z" fill="#F7A93E"/><path d="M9 27a6 6 0 0 1 3-11 9 9 0 0 1 12-7 9 9 0 0 1 12 7 6 6 0 0 1 3 11z" fill="COLOR"/><circle cx="24" cy="7" r="4" fill="#B0124F"/></svg>';
const SHAPES = [HEART, STAR, SPRINKLE, CUPCAKE, HEART, SPRINKLE, STAR];
const COLORS = ['#D91F6B', '#E7739F', '#F7A93E', '#B0124F', '#F9A8C8'];
const TREAT_COUNT = 26;
const RETRY_DELAYS = [500, 900, 1400, 2000];

function findInShadows(root, selector) {
  const found = root.querySelector(selector);
  if (found) return found;
  for (const element of root.querySelectorAll('*')) {
    if (element.shadowRoot) {
      const inner = findInShadows(element.shadowRoot, selector);
      if (inner) return inner;
    }
  }
  return null;
}

class TreatShower extends HTMLElement {
  connectedCallback() {
    this.wasOpen = { rewards: false, chat: false };
    this.lastShower = { rewards: 0, chat: 0 };
    this.timer = setInterval(() => this.watch(), 300);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  chatRoot() {
    const chat = document.querySelector('shopify-chat');
    return chat && chat.shadowRoot ? chat.shadowRoot : null;
  }

  rewardsWindow() {
    const frame = document.getElementById('marsello-widget');
    if (!frame || frame.classList.contains('hide')) return null;
    const rect = frame.getBoundingClientRect();
    const onScreen = rect.right > 40 && rect.left < window.innerWidth - 40;
    return rect.width > 150 && rect.height > 150 && onScreen ? frame : null;
  }

  chatWindow() {
    // The chat window is the ".panel" inside the chat's shadow roots.
    const root = this.chatRoot();
    const panel = root ? findInShadows(root, '.panel') : null;
    if (!panel) return null;
    const style = getComputedStyle(panel);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) < 0.5) return null;
    const rect = panel.getBoundingClientRect();
    const onScreen = rect.bottom > 40 && rect.top < window.innerHeight - 40;
    return rect.width > 150 && rect.height > 150 && onScreen ? panel : null;
  }

  windowFor(name) {
    return name === 'rewards' ? this.rewardsWindow() : this.chatWindow();
  }

  bindTaps() {
    const tab = document.getElementById('marsello-tab');
    const tabDoc = tab ? tab.contentDocument : null;
    if (tabDoc && tabDoc.documentElement && !tabDoc.documentElement.dataset.cccShower) {
      tabDoc.documentElement.dataset.cccShower = 'bound';
      tabDoc.addEventListener('click', () => this.soon('rewards'));
    }
    const root = this.chatRoot();
    const activator = root ? findInShadows(root, '.activator') : null;
    if (activator && !activator.dataset.cccShower) {
      activator.dataset.cccShower = 'bound';
      activator.addEventListener('click', () => this.soon('chat'));
    }
  }

  soon(name) {
    RETRY_DELAYS.forEach((delay) => {
      setTimeout(() => {
        const target = this.windowFor(name);
        if (target) this.shower(name, target);
      }, delay);
    });
  }

  // Both launchers float on the same clock (start time 0 on the page timeline), so they stay level.
  floatInStep() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const root = this.chatRoot();
    const launchers = [document.getElementById('marsello-tab'), root ? findInShadows(root, '.activator') : null];
    launchers.forEach((launcher) => {
      if (!launcher || launcher.dataset.cccFloat) return;
      launcher.dataset.cccFloat = 'on';
      const float = launcher.animate(
        [{ transform: 'translateY(0)' }, { transform: 'translateY(-10px)' }, { transform: 'translateY(0)' }],
        { duration: 4500, iterations: Infinity, easing: 'ease-in-out' }
      );
      float.startTime = 0;
    });
  }

  watch() {
    try {
      this.bindTaps();
      this.floatInStep();
    } catch (error) {
      // A frame that isn't ready yet; try again on the next tick.
    }
    ['rewards', 'chat'].forEach((name) => {
      const isOpen = Boolean(this.windowFor(name));
      if (isOpen && !this.wasOpen[name]) this.soon(name);
      this.wasOpen[name] = isOpen;
    });
  }

  shower(name, target) {
    const now = Date.now();
    if (now - this.lastShower[name] < 2500) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const rect = target.getBoundingClientRect();
    if (rect.width < 100 || rect.height < 100) return;
    this.lastShower[name] = now;

    // Inline styles so the layer works inside the chat's shadow root too, where theme CSS can't reach.
    const layer = document.createElement('div');
    layer.className = 'treat-shower';
    layer.setAttribute('aria-hidden', 'true');
    Object.assign(layer.style, {
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: '2147483647',
      borderRadius: '20px',
    });
    if (name === 'chat') {
      // Inside the chat window itself, so it sits above the chat's own content.
      Object.assign(layer.style, { position: 'absolute', inset: '0' });
      target.appendChild(layer);
    } else {
      Object.assign(layer.style, {
        position: 'fixed',
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
      });
      document.body.appendChild(layer);
    }

    let longest = 0;
    for (let index = 0; index < TREAT_COUNT; index += 1) {
      const treat = document.createElement('span');
      treat.className = 'treat-shower__treat';
      const size = 11 + Math.random() * 11;
      treat.innerHTML = SHAPES[index % SHAPES.length]
        .replace('COLOR', COLORS[index % COLORS.length])
        .replace('<svg ', '<svg style="display:block;width:100%;height:100%" ');
      treat.style.position = 'absolute';
      treat.style.top = '0';
      treat.style.display = 'block';
      treat.style.width = `${size}px`;
      treat.style.height = `${size}px`;
      treat.style.left = `${4 + Math.random() * 90}%`;
      layer.appendChild(treat);

      const tilt = Math.random() * 60 - 30;
      const sway = Math.random() * 36 - 18;
      const duration = 2000 + Math.random() * 1400;
      const delay = Math.random() * 1000;
      longest = Math.max(longest, duration + delay);
      treat.animate(
        [
          { transform: `translate(0, -24px) rotate(${tilt}deg)`, opacity: 0 },
          { opacity: 1, offset: 0.1 },
          { transform: `translate(${sway}px, ${rect.height * 0.5}px) rotate(${tilt + 120}deg)`, opacity: 1, offset: 0.55 },
          { transform: `translate(${-sway}px, ${rect.height + 24}px) rotate(${tilt + 260}deg)`, opacity: 0 },
        ],
        { duration, delay, easing: 'cubic-bezier(.35,.1,.6,1)', fill: 'both' }
      );
    }
    setTimeout(() => layer.remove(), longest + 200);
  }
}

if (!customElements.get('treat-shower')) customElements.define('treat-shower', TreatShower);
